// BridgeInbox — Vendor Bridge outbound status sync.
// Shows every WO that came in via an integration (Maintenex, ServiceChannel,
// Corrigo, etc.) and lets the dispatcher fire back a status reply so the
// panel stays in sync without swivel-chairing.
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PlugsConnected, PaperPlaneTilt, Warning, CheckCircle, Clock, Prohibit } from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { key: "scheduled",   label: "Scheduled",   icon: <Clock       weight="fill" size={14}/>, color: "text-cyan-700 bg-cyan-100" },
  { key: "in_progress", label: "In Progress", icon: <Clock       weight="fill" size={14}/>, color: "text-fuchsia-700 bg-fuchsia-100" },
  { key: "on_hold",     label: "On Hold",     icon: <Warning     weight="fill" size={14}/>, color: "text-amber-700 bg-amber-100" },
  { key: "completed",   label: "Completed",   icon: <CheckCircle weight="fill" size={14}/>, color: "text-emerald-700 bg-emerald-100" },
  { key: "declined",    label: "Declined",    icon: <Prohibit    weight="fill" size={14}/>, color: "text-red-700 bg-red-100" },
];

export default function BridgeInbox() {
  const [jobs, setJobs] = useState(null);
  const [replying, setReplying] = useState(null); // job id
  const [form, setForm] = useState({ status: "scheduled", eta: "", note: "" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    const r = await api.get("/integrations/bridge/inbox");
    setJobs(r.data.jobs || []);
  };
  useEffect(() => { load(); }, []);

  const openReply = (j) => {
    setReplying(j.id);
    setForm({ status: j.bridge_last_status || "scheduled", eta: "", note: "" });
  };
  const closeReply = () => setReplying(null);

  const send = async () => {
    setSending(true);
    try {
      await api.post(`/integrations/bridge/${replying}/status-reply`, form);
      toast.success("Status sent back to the CMMS");
      setReplying(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed"));
    } finally { setSending(false); }
  };

  if (jobs === null) return <div className="p-6 text-slate-500">Loading bridge inbox…</div>;

  return (
    <div className="p-4 md:p-6" data-testid="bridge-inbox-page">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-xl bg-[#0f172a] text-white flex items-center justify-center">
          <PlugsConnected weight="fill" size={22} className="text-fuchsia-400"/>
        </div>
        <div>
          <h1 className="font-display font-black text-3xl leading-none">Bridge Inbox</h1>
          <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
            Push status back to Maintenex · ServiceChannel · Corrigo · FMX
          </div>
        </div>
      </div>

      <p className="text-sm text-slate-600 mb-5 max-w-2xl">
        Every WO that landed here from a CMMS panel shows below. Tap
        <b> Reply Status</b> to send an ETA or completion note back to the
        origin — no swivel-chair updates.
      </p>

      {jobs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400" data-testid="bridge-inbox-empty">
          No bridged work orders yet. When Maintenex/ServiceChannel/Corrigo emails
          hit <span className="font-mono text-slate-600">intake@fieldradar.pro</span>,
          they'll appear here.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
          {jobs.map(j => {
            const statusMeta = STATUS_OPTIONS.find(s => s.key === j.bridge_last_status);
            return (
              <div key={j.id} data-testid={`bridge-row-${j.id}`}
                className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600 bg-fuchsia-50 px-1.5 py-0.5 rounded">
                      {j.vendor_key || "unknown"}
                    </span>
                    {j.external_wo_number && (
                      <span className="text-xs font-mono font-black text-slate-700">
                        {j.external_wo_number}
                      </span>
                    )}
                    {j.paste_mode && (
                      <span className="text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                        paste-mode
                      </span>
                    )}
                    {statusMeta && (
                      <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${statusMeta.color}`}>
                        {statusMeta.icon} {statusMeta.label}
                      </span>
                    )}
                  </div>
                  <div className="font-bold text-sm mt-1 truncate">{j.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">{j.address}</div>
                  {j.inbound_email && (
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      From: <span className="font-mono">{j.inbound_email}</span>
                    </div>
                  )}
                  {j.bridge_last_sent_at && (
                    <div className="text-[10px] text-emerald-600 mt-0.5">
                      ✓ Last reply {new Date(j.bridge_last_sent_at).toLocaleString()}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0">
                  <button onClick={() => openReply(j)} data-testid={`bridge-reply-btn-${j.id}`}
                    disabled={j.paste_mode || !j.inbound_email}
                    title={j.paste_mode
                      ? "Paste-mode WO — no inbound email to reply to. Send status manually."
                      : !j.inbound_email ? "No inbound email captured" : ""}
                    className="h-9 px-4 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed shadow">
                    <PaperPlaneTilt weight="fill" size={12}/> Reply Status
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reply modal */}
      {replying && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4"
          onClick={closeReply} data-testid="bridge-reply-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl">
            <div className="font-display font-black text-2xl mb-1">Reply Status</div>
            <div className="text-xs text-slate-500 mb-4">
              This will email the CMMS that originally sent us this WO with the fields below.
            </div>
            <label className="block mb-3">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Status</div>
              <div className="grid grid-cols-2 gap-2">
                {STATUS_OPTIONS.map(o => (
                  <button key={o.key} type="button"
                    onClick={() => setForm(f => ({ ...f, status: o.key }))}
                    data-testid={`bridge-status-${o.key}`}
                    className={`h-10 rounded-lg border-2 text-xs font-black uppercase tracking-widest inline-flex items-center justify-center gap-1 ${
                      form.status === o.key ? "border-[#00C4CC] bg-[#00C4CC]/10 text-[#00C4CC]" : "border-slate-200 hover:border-slate-400 text-slate-700"
                    }`}>
                    {o.icon} {o.label}
                  </button>
                ))}
              </div>
            </label>
            <label className="block mb-3">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">ETA (optional)</div>
              <input value={form.eta} onChange={e => setForm(f => ({ ...f, eta: e.target.value }))}
                placeholder="Tomorrow 9-11 AM" data-testid="bridge-eta"
                className="w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </label>
            <label className="block mb-4">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Note (optional)</div>
              <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                rows={3} placeholder="Tech Mike will be onsite tomorrow morning. Parts are stocked."
                data-testid="bridge-note"
                className="w-full px-3 py-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
            </label>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={closeReply} className="h-10 px-4 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase tracking-widest text-xs rounded-lg" data-testid="bridge-reply-cancel">
                Cancel
              </button>
              <button onClick={send} disabled={sending} data-testid="bridge-reply-send"
                className="h-10 px-5 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50 shadow">
                <PaperPlaneTilt weight="fill" size={12}/> {sending ? "Sending…" : "Send Reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
